import type { ProductFormulation } from '@/constants/products';
import type { DispenseLimitsByFormulation } from './dispenseLimit';
import type { DispenseLimitRow } from './getFormulationDispenseLimitRows';

const pickDispenseLimits = (
  dispenseLimits: DispenseLimitsByFormulation,
  isKept: (formulation: string) => boolean,
): DispenseLimitsByFormulation => {
  const entries = Object.entries(dispenseLimits);
  const keptEntries = entries.filter(([formulation]) => isKept(formulation));
  // The same object when nothing is dropped, so the store keeps it and its readers see no change
  return keptEntries.length === entries.length ? dispenseLimits : Object.fromEntries(keptEntries);
};

/**
 * The set limits of the formulations that still have a row. A limit whose formulation lost its last product would
 * otherwise come back, still confirmed, on the next product of that formulation.
 */
export const keepDispenseLimitsWithRows = (
  dispenseLimits: DispenseLimitsByFormulation,
  rows: DispenseLimitRow[],
): DispenseLimitsByFormulation => {
  const formulationsWithRows = new Set<string>(rows.map(({ formulation }) => formulation));
  return pickDispenseLimits(dispenseLimits, (formulation) => formulationsWithRows.has(formulation));
};

/** The set limits without those of the given formulations */
export const omitDispenseLimits = (
  dispenseLimits: DispenseLimitsByFormulation,
  formulations: ProductFormulation[],
): DispenseLimitsByFormulation => {
  const omitted = new Set<string>(formulations);
  return pickDispenseLimits(dispenseLimits, (formulation) => !omitted.has(formulation));
};
